import { type LogtoConfig, Prompt, UserScope } from '@logto/react';
import { openLink, retrieveLaunchParams } from '@tma.js/sdk-react';

import { env } from '@/env';
import { shouldRefreshAndRetryExchange } from '@/lib/exchangeRetry';

// Qriots audience constants — declared before logtoConfig so they can be
// referenced in the resources array and anywhere else in this file.
const QRIOTS_ACCESS = 'qlash.qriots_access_token';
const QRIOTS_EXP = 'qlash.qriots_token_exp';
const QRIOTS_RESOURCE = 'https://qriots.qlash.gg/api';

// QBot audience — same resource as the backoffice, scoped storage keys.
const QBOT_ACCESS = 'qlash.qbot_access_token';
const QBOT_EXP = 'qlash.qbot_token_exp';
const QBOT_RESOURCE = 'https://qbot.qlash.gg';

// QSocials audience — dedicated Logto API resource for the social
// microservice (friends/follows/presence/watch/cheers).
const QSOCIALS_ACCESS = 'qlash.qsocials_access_token';
const QSOCIALS_EXP = 'qlash.qsocials_token_exp';
const QSOCIALS_RESOURCE = 'https://qsocials.qlash.gg';

// QDuels audience — Logto API resource for the 1v1 duels microservice.
// Same `/api`-suffixed audience convention as qriots; scoped storage keys.
const QDUELS_ACCESS = 'qlash.qduels_access_token';
const QDUELS_EXP = 'qlash.qduels_token_exp';
const QDUELS_RESOURCE = 'https://qduels.qlash.gg/api';

// QChess audience — Logto API resource for the chess microservice (Lichess
// tournaments + duels + QLASH leaderboard). Same `/api`-suffixed audience
// convention as qriots/qduels; scoped storage keys.
const QCHESS_ACCESS = 'qlash.qchess_access_token';
const QCHESS_EXP = 'qlash.qchess_token_exp';
const QCHESS_RESOURCE = 'https://qchess.qlash.gg/api';

// QClans audience — Logto API resource for the Clash of Clans microservice
// (stats + tracking + competitions + AI coaching). Same `/api`-suffixed
// audience convention as qriots/qduels/qchess; scoped storage keys.
const QCLANS_ACCESS = 'qlash.qclans_access_token';
const QCLANS_EXP = 'qlash.qclans_token_exp';
const QCLANS_RESOURCE = 'https://qclans.qlash.gg/api';

// QBank — virtual currency / loyalty. Logto resource = the dedicated "Qlash API
// QBank" (aud https://qbank.qlash.gg/api, resource id qreuizx76cehrbc9u287w),
// whose scopes are named like QBank's qbank:* roles (no scope->role bridge needed).
// Migrated 2026-06-02 off the legacy orphan resource https://wallet.qlash.gg.
// NB: the audience identifier is decoupled from the serving URL — the API is still
// served at https://wallet.qlash.gg (env.qbankApiUrl). In DEV the token is minted
// from QBank's internal test OIDC (see getQbankAccessToken below).
const QBANK_ACCESS = 'qlash.qbank_access_token';
const QBANK_EXP = 'qlash.qbank_token_exp';
const QBANK_RESOURCE = 'https://qbank.qlash.gg/api';

// QGammon audience — Logto API resource for the real-time backgammon vertical
// (gameserver :4001 + qgammon-api :4000). Same `/api`-suffixed audience
// convention as qriots/qduels/qchess/qclans; scoped storage keys. The resource
// `Qlash API QGammon` exists in PROD Logto since 2026-06-11 (the QGammon
// backoffice SPA mints tokens for it), so it is SAFE to add to the authorize
// request — see the GOLDEN RULE note on QGAMMON_RESOURCE below.
const QGAMMON_ACCESS = 'qlash.qgammon_access_token';
const QGAMMON_EXP = 'qlash.qgammon_token_exp';
// ⚠️ Il valore DIPENDE DAL TENANT ed e' definito piu' sotto, dopo `isStagingTenant`:
// su `stg-account` la resource di produzione NON esiste, e chiederla romperebbe il login.

// QChinesePoker audience — Logto API resource for the heads-up Pineapple OFC
// vertical (qcp-gameserver Socket.IO :4101 + qcp-api REST :4100). Same
// `/api`-suffixed audience convention as qriots/qduels/qchess/qclans/qgammon;
// scoped storage keys. The resource "Qlash API QChinesePoker" is registered in
// PROD Logto since 2026-06-17 (aud https://qchinesepoker.qlash.gg/api, ES384),
// so it is SAFE to add to the authorize request — see the GOLDEN RULE note on
// QCP_RESOURCE in buildAuthUrl below. Gameplay is audience-only (no scope
// enforced); only the admin seed-verifier route requires qchinesepoker:admin.
const QCP_ACCESS = 'qlash.qcp_access_token';
const QCP_EXP = 'qlash.qcp_token_exp';
const QCP_RESOURCE = 'https://qchinesepoker.qlash.gg/api';
const QUEST_ACCESS = 'qlash.quest_access_token';
const QUEST_EXP = 'qlash.quest_token_exp';
// Quest (missioni & engagement). La resource "Qlash API Quest"
// (`f736b1uq7dj69ig99kp3i`) esiste in PROD Logto: VERIFICATA alla fonte con la
// Management API (programma quest-orc, 2026-08-27/28), non piu' la sola
// dichiarazione di `Quest/STATUS.md`. Regola d'oro soddisfatta ⇒ dal 2026-08-29
// (autorizzazione esplicita di Luca) QUEST_RESOURCE sta in
// `logtoConfig.resources[]` E nella authorize call di buildAuthUrl().
// I due modi di fallire restano coperti: la resource ESISTE, quindi niente
// `invalid_target` (l'incidente QClans 2026-06-01 nasceva da una resource
// inesistente); se mancasse il grant server-side all'app TMA, l'audience viene
// droppata in silenzio e getQuestAccessToken() torna null → la sezione degrada
// a "non disponibile": fallimento visibile e circoscritto, mai del sign-in.
const QUEST_RESOURCE = 'https://quest.qlash.gg/api';

/**
 * The microservice resources exist only on the production Logto tenant. The
 * staging tenant was probed on 2026-09-02: requesting any of the unavailable
 * resources fails the whole authorize request with `invalid_target`.
 *
 * Keep the fallback production-safe: reduce the list only when the endpoint is
 * explicitly the staging tenant. A missing or malformed endpoint must never
 * silently remove production audiences.
 */
const STAGING_LOGTO_HOST = 'stg-account.qlash.gg';

const isStagingTenant = (() => {
  try {
    return new URL(env.logtoEndpoint).hostname === STAGING_LOGTO_HOST;
  } catch {
    return false;
  }
})();

/**
 * 🔴 QGammon: DUE resource, una per tenant — e la scelta sbagliata rompe il login, non la sezione.
 *
 * `https://qgammon.qlash.gg/api` esiste **solo sul tenant di produzione**;
 * `https://stg-qgammon.qlash.gg/api` **solo su quello di staging** (creata il 2026-09-06,
 * ADR-0108 · L-210). Chiedere all'`authorize` una resource che sul tenant non esiste
 * produce `invalid_target` e **il login smette di funzionare per TUTTI** su quel tenant —
 * non solo per il backgammon. Successo il 2026-06-01 con `QCLANS_RESOURCE`.
 *
 * ⚠️ Distinzione che vale la pena tenere a mente perche' i due errori si scrivono uguale:
 * una resource **registrata ma non concessa** viene ignorata in silenzio e rompe un solo
 * refresh; una che **non esiste** rompe tutto.
 *
 * ✅ Condizione verificata prima di aggiungerla `[M or49, Management API di stg-account,
 * 2026-09-06]`: la resource di staging **esiste** (`id 68ck5129yozn2oau7oam2`), ha tre scope
 * (`qgammon:read/write/admin`) ed **e' concessa** ai ruoli `qgammon-player`,
 * `qgammon-admin` e `beta`.
 * 📌 *La registrazione viene PRIMA della riga di codice, sempre. Questa nota esiste perche'
 * chi leggera' questa riga fra sei mesi sappia che l'ordine e' stato rispettato, e non lo
 * deduca dal fatto che il login funziona.*
 */
const QGAMMON_RESOURCE = isStagingTenant
  ? 'https://stg-qgammon.qlash.gg/api'
  : 'https://qgammon.qlash.gg/api';

const STAGING_RESOURCES: string[] = [
  env.logtoResource,
  QBOT_RESOURCE,
  // Aggiunta il 2026-09-06 (GO di Luca): lo staging QGammon esiste ed e' agganciato a
  // questo tenant. ⚠️ Su staging la lista resta CORTA di proposito — ogni voce in piu' e'
  // una superficie in cui `invalid_target` puo' rompere il login di tutti i seggi che
  // lavorano qui.
  QGAMMON_RESOURCE,
];

const ALL_RESOURCES: string[] = [
  env.logtoResource,
  QRIOTS_RESOURCE,
  QBOT_RESOURCE,
  QSOCIALS_RESOURCE,
  QDUELS_RESOURCE,
  QCHESS_RESOURCE,
  QCLANS_RESOURCE,
  QBANK_RESOURCE,
  QGAMMON_RESOURCE,
  QCP_RESOURCE,
  QUEST_RESOURCE,
];

export const AUTHORIZED_RESOURCES: string[] = isStagingTenant
  ? STAGING_RESOURCES
  : ALL_RESOURCES;

/**
 * Logto OIDC configuration for the QLASH Telegram Mini App.
 *
 * Values mirror the mobile-app feat/new-logto branch (src/services/logto.ts):
 * the same staging tenant, app, and API resource — so the access token issued
 * by Logto authenticates against the existing Laravel backend (which validates
 * via JWKS). Production values are picked up from the env when VITE_LOGTO_*
 * vars are set; defaults fall back to staging.
 */
export const logtoConfig: LogtoConfig = {
  endpoint: env.logtoEndpoint,
  appId: env.logtoAppId,
  resources: AUTHORIZED_RESOURCES,
  scopes: [
    UserScope.CustomData,
    UserScope.Email,
    UserScope.Identities,
    UserScope.Profile,
    UserScope.Roles,
    'offline_access',
    // QBank API-resource scopes. Logto only embeds resource scopes in the
    // access token if they are REQUESTED here (RBAC grant alone is not
    // enough — unrequested scopes are omitted and the token's `scope` claim
    // comes back empty). QBank is the first backend that actually enforces
    // scopes on user tokens (qriots/qchess/qduels only validate audience),
    // which is why only QBank ever surfaced this. Scopes the user's roles
    // don't grant are silently dropped by Logto, so requesting them is safe
    // for every user tier. Read-only — the TMA never writes to QBank.
    'qbank:wallet:read',
    'qbank:league:read',
    // QGammon resource scopes. The gameserver enforces qgammon:read/write on
    // user tokens (audience https://qgammon.qlash.gg/api) and provisions a
    // per-user row by logtoSub — this is the fix for the shared dev-bypass
    // identity bug. Request read+write (NOT admin); scopes the user's roles
    // don't grant are silently dropped by Logto, so requesting them is safe for
    // every tier (same reasoning as the qbank scopes above).
    'qgammon:read',
    'qgammon:write',
    // Quest resource scope. Quest enforces SCOPES on the user token, not just
    // the audience (third service to do so, after QBank and QGammon) — and
    // PR #79 added QUEST_RESOURCE WITHOUT this line, which is exactly the
    // failure the note above `getQuestAccessToken()` had predicted: the token
    // came back with the right `aud` and an EMPTY `scope` claim, and Quest
    // answered `403 {"missing":["quest:missions:read"]}`. Measured on the live
    // tenant 2026-08-29 23:16 UTC — the resource, the scope, the `quest-player`
    // role and its assignment were all already correct in Logto: nothing there
    // needed changing, the gap was here.
    //
    // FULL name, with the service prefix: a token carrying `missions:read`
    // passes signature validation and still takes a 403.
    //
    'quest:missions:read',
    // The claim scope. It landed 2026-08-30 with the claim button, exactly as the
    // line it replaces had said it would ("when the claim button lands, its
    // sibling line goes here AND in SCOPE_STRING — the two are mirrors"). Until
    // then it was deliberately absent: a scope nothing spends is authority held
    // for no reason. Now something spends it — `POST /v1/missions/:id/claim`,
    // guarded by `requireScopes(['quest:missions:claim'])` in Quest's server.ts
    // @ f64873a. Nothing to do on the Logto side: the `quest-player` role already
    // owns read AND claim (verified on the live PROD tenant), so this line is the
    // whole change.
    //
    // 🔴 PRECONDITION THIS LINE CANNOT SATISFY BY ITSELF: a refresh token minted
    // before this release does NOT carry the new scope, and no refresh grant can
    // add it — the scope set is fixed at the authorize call. Every user therefore
    // needs ONE fresh interactive sign-in after this ships, or the button takes a
    // 403. That failure is not silent by design: `claimOutcome` maps 401/403 to
    // its own `reauth` face ("sign in again to claim") instead of the generic
    // retry copy, precisely so the remedy is on screen rather than in a log.
    'quest:missions:claim',
  ],
  // Force re-authentication AND consent on every interactive sign-in
  // (standalone web / SDK path), mirroring `prompt=login consent` in
  // buildAuthUrl() for the Telegram bridge. `Login` re-prompts credentials
  // after a logout; `Consent` re-generates the stored grant so newly-added API
  // resources (qsocials, qduels) are authorized — otherwise their on-demand
  // refresh grant 400s and the token comes back null.
  prompt: [Prompt.Login, Prompt.Consent],
};

export const API_RESOURCE = env.logtoResource;

const SCOPE_STRING = [
  'openid',
  'offline_access',
  'profile',
  'email',
  UserScope.CustomData,
  UserScope.Identities,
  UserScope.Roles,
  // QBank resource scopes — must be requested explicitly or Logto issues the
  // qbank-audience token with an EMPTY `scope` claim (see logtoConfig.scopes
  // note). Mirrors the SDK config for the Telegram-bridge manual PKCE flow.
  'qbank:wallet:read',
  'qbank:league:read',
  // QGammon resource scopes — mirror of logtoConfig.scopes for the
  // Telegram-bridge manual PKCE flow. Must be requested explicitly or Logto
  // issues the qgammon-audience token with an EMPTY `scope` claim.
  'qgammon:read',
  'qgammon:write',
  // Quest resource scopes — mirror of logtoConfig.scopes. This is the path the
  // TMA actually takes in production (the Telegram bridge builds the authorize
  // URL by hand), so a scope missing HERE is the one the live token misses:
  // the 2026-08-29 `scope: ""` was observed on a grant built from this very
  // string. `claim` joined `read` on 2026-08-30 with the claim button — see the
  // long note on its twin in logtoConfig.scopes, including why every user needs
  // one fresh interactive sign-in before the button can work.
  'quest:missions:read',
  'quest:missions:claim',
].join(' ');

/* ------------------------------------------------------------------ */
/* Manually-managed token storage (Telegram external-browser flow)    */
/* ------------------------------------------------------------------ */
//
// Google blocks OAuth in embedded WebViews ("disallowed_useragent"). To work
// around it we run the OIDC flow in the user's *system* browser via
// `WebApp.openLink`, then bridge the auth code back via a `t.me` deep link
// with the code in `start_param`. The mini-app then exchanges the code for
// tokens manually and stores them under these keys. RTK Query's
// `prepareHeaders` reads from this storage first, falling back to the Logto
// SDK if no manual token is present (covers the standalone web flow).

const QLASH_ACCESS = 'qlash.access_token';
const QLASH_REFRESH = 'qlash.refresh_token';
const QLASH_EXP = 'qlash.token_exp';

/**
 * Cap on the Logto SDK's getAccessToken. Generous enough for a real network
 * round-trip, short enough that a hung SDK resolves to "no token" and the auth
 * gate falls through to Login instead of spinning.
 */
const SDK_TOKEN_TIMEOUT_MS = 6000;
const QLASH_ID_TOKEN = 'qlash.id_token';
const PKCE_VERIFIER = 'qlash.pkce.verifier';
const PKCE_STATE = 'qlash.pkce.state';
// ⚠️ La chiave e' `.v2` per un motivo che non e' cosmetico (L-163, or43 05/09): la `v1`
// (`qlash.bridge_debug`) sui dispositivi gia' passati di qui contiene frammenti della
// risposta del token endpoint. Cambiare chiave + cancellare la vecchia al primo boot
// ripulisce OGNI client **senza che noi si debba leggere cosa c'e' dentro** — e la
// pulizia non dipende dall'indovinare la firma di cio' che e' trapelato.
// 📌 *Una chiave che si chiama «debug» e' il posto che nessuno audita, perche' il nome
// dice «temporaneo». Il contenuto invece e' una sessione vera, in produzione.*
const BRIDGE_DEBUG_LEGACY = 'qlash.bridge_debug';
const BRIDGE_DEBUG = 'qlash.bridge_debug.v2';
try {
  localStorage.removeItem(BRIDGE_DEBUG_LEGACY);
} catch {
  /* localStorage indisponibile (private mode, storage bloccato): non e' un errore fatale */
}
const BACKEND_TOKEN = 'qlash.backend_token';
// JWT interno Limulo. La costante pubblica vive in `lib/limulo/auth.ts`, ma qui
// teniamo il literal nel teardown centrale per evitare che un account nuovo erediti
// la sessione BFF del precedente.
const LIMULO_SESSION = 'qlash.limulo.session';
// Dedup key for the Telegram auth-code bridge. Owned by useLogtoAuth (which
// re-declares the same literal), exported here so clearStoredTokens can wipe it
// on logout — otherwise a stale already-consumed code survives sign-out and the
// next sign-in's consume short-circuits ("skipping already-consumed code"),
// leaving the user without fresh tokens.
export const LAST_CONSUMED_AUTH_CODE = 'qlash.last_consumed_auth_code';

export function getBridgeDebug(): string | null {
  return localStorage.getItem(BRIDGE_DEBUG);
}
export function clearBridgeDebug(): void {
  localStorage.removeItem(BRIDGE_DEBUG);
}
/**
 * ⚠️ ESPORTATA il 2026-09-05 per un motivo preciso: la sessione poteva morire **senza
 * lasciare traccia**. `handleDeadGrant` scrive la sua riga; il ramo di
 * `useLogtoAuth` che cancella i token dopo un refresh fallito no — ed e' quello che e'
 * stato usato due volte in una sera, su due profili separati, lasciando un registro che
 * racconta solo cosa accadeva PRIMA.
 * 📌 *Il registro esiste per raccontare la vita della sessione, e l'evento che la termina
 * era l'unico che non registrava.*
 *
 * ⚠️ Chi la usa scriva FATTI, non contenuti: l'APERTO su questo registro e' che accumula
 * claim decodificati dei token. Una riga come «sessione cancellata: refresh fallito» non
 * aggiunge nulla di sensibile; una che stampi un token si'.
 */
/** Chiavi JSON il cui VALORE concede accesso: si redigono sempre, ovunque compaiano. */
const SECRET_KEYS =
  'refresh_token|access_token|id_token|grantId|client_secret|code_verifier|authorization|password|secret';
/** JWT: tre segmenti base64url separati da punto, il primo comincia per `eyJ`. */
const JWT_RE = /\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]*/g;
const SECRET_JSON_RE = new RegExp(`("(?:${SECRET_KEYS})"\\s*:\\s*")[^"]+(")`, 'gi');
/** Corse opache lunghe: in un registro di diagnostica il verso sicuro e' redigere. */
const OPAQUE_RE = /\b[A-Za-z0-9_-]{32,}\b/g;

/**
 * ⚠️ Redazione ALLA FONTE (L-163). Non filtra «i segreti che ci aspettiamo»: filtra le
 * FORME che un segreto ha. E' la differenza fra un promemoria e un meccanismo — chi
 * aggiungera' la prossima `dbg()` non stara' pensando a questa riga.
 *
 * ⚠️ Cosa NON toglie, detto perche' nessuno lo scopra dopo: i claim decodificati
 * (`sub`, `email`) restano. Sono identificatori, non credenziali: toglierli spegnerebbe
 * proprio la diagnosi di identita' per cui il registro esiste. E' un APERTO dichiarato,
 * non una svista.
 */
export function redactForLog(msg: string): string {
  return msg
    .replace(SECRET_JSON_RE, (_m, k: string, q: string) => `${k}<redatto>${q}`)
    .replace(JWT_RE, (m) => `<jwt:${m.length}>`)
    .replace(OPAQUE_RE, (m) => `<opaco:${m.length}>`);
}

export function dbg(msg: string): void {
  const safe = redactForLog(msg);
  // Anche la console e' redatta: uno screenshot di supporto la trasporta esattamente come
  // il localStorage, e la sede prevista di un segreto non e' nessuna delle due.
  console.info('[bridge]', safe);
  const prev = localStorage.getItem(BRIDGE_DEBUG) || '';
  const stamp = new Date().toISOString().slice(11, 19);
  const next = `${prev}${stamp} ${safe}\n`;
  localStorage.setItem(BRIDGE_DEBUG, next.slice(-2000));
}

import { resourceMemo } from './resourceMemo';
import { fetchWithTimeout, TOKEN_FETCH_TIMEOUT_MS } from './fetchWithTimeout';

interface TokenSet {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  id_token?: string;
}

function persistTokens(tokens: TokenSet): void {
  localStorage.setItem(QLASH_ACCESS, tokens.access_token);
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QLASH_EXP, String(expAt));
  // ⚠️ 2026-09-06 — L'UNICO SEGNALE che i token sono cambiati. Chi ascolta:
  // `lib/roleRevalidation.ts` (nome dell'evento: `TOKENS_CHANGED_EVENT`), da cui
  // `useHasLogtoRole` rilegge il claim `roles`. Senza questa riga la sezione beta resta
  // VUOTA per un utente a cui il ruolo e' appena stato assegnato, e rende solo al
  // caricamento successivo (misurato su stg-tma da or45 il 06/09).
  // 🔴 Non sostituibile con l'evento `storage` del browser: quello NON si attiva nella
  // scheda che ha scritto, e la WebView di Telegram ha una scheda sola.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('qlash:tokens-changed'));
  }
}

// In-memory cache of a no-resource (OP) access token used for the userinfo
// endpoint (which returns `identities`, unlike a resource-scoped JWT). Cleared
// alongside the per-service caches on logout/account-switch so it can't leak the
// previous user's identities.
let opTokenCache: { token: string; exp: number } | null = null;

// Per-service resource token caches (audience-scoped access tokens minted from
// the shared refresh_token). Cleared on logout AND on every fresh interactive
// login (exchangeAuthCode) so an account-switch can't inherit the previous
// user's per-service tokens (e.g. acting as the old user on qgammon until they
// expire). Keep this list in sync when a new per-service resource is added.
//
// ⚠️ BACKEND_TOKEN belongs here too, and is the one that matters most: it is the
// Laravel Passport token behind /users/me, tournaments, chat and matches — the
// most identity-bearing credential we cache. getApiAccessToken() returns it
// unconditionally before re-exchanging, so a stale one is used verbatim: leaving
// it out let a fresh login on account B keep acting as account A on the monolith,
// which is exactly what this teardown exists to prevent.
const PER_SERVICE_TOKEN_KEYS = [
  BACKEND_TOKEN,
  LIMULO_SESSION,
  QRIOTS_ACCESS, QRIOTS_EXP,
  QBOT_ACCESS, QBOT_EXP,
  QSOCIALS_ACCESS, QSOCIALS_EXP,
  QDUELS_ACCESS, QDUELS_EXP,
  QCHESS_ACCESS, QCHESS_EXP,
  QCLANS_ACCESS, QCLANS_EXP,
  QBANK_ACCESS, QBANK_EXP,
  QGAMMON_ACCESS, QGAMMON_EXP,
  // QChinesePoker resource token cache (added on feat/qcp-multihand-tma) — kept
  // in the shared wipe list so a logout / account switch drops it too.
  QCP_ACCESS, QCP_EXP,
  // Cache del token Quest. Sta nella lista condivisa perche' al cambio account
  // deve morire con gli altri: su una superficie che mostra (e in fase 2
  // riscuote) ricompense, un token sopravvissuto significa attribuire la
  // ricompensa alla persona sbagliata.
  QUEST_ACCESS, QUEST_EXP,
];

function clearPerServiceTokenCaches(): void {
  for (const k of PER_SERVICE_TOKEN_KEYS) localStorage.removeItem(k);
  opTokenCache = null;
  // La memoria delle resource assenti e' relativa al TENANT, e un cambio account puo'
  // portare su un tenant diverso: tenerla sarebbe negare a un utente nuovo una resource che
  // per lui esiste. Si dimentica con le altre cache.
  resourceMemo.clear();
}

export function clearStoredTokens(): void {
  localStorage.removeItem(QLASH_ACCESS);
  localStorage.removeItem(QLASH_REFRESH);
  localStorage.removeItem(QLASH_EXP);
  localStorage.removeItem(QLASH_ID_TOKEN);
  localStorage.removeItem(PKCE_VERIFIER);
  localStorage.removeItem(PKCE_STATE);
  localStorage.removeItem(BACKEND_TOKEN);
  clearPerServiceTokenCaches();
  // Wipe the auth-code dedup marker so the next sign-in always exchanges its
  // fresh `auth_<code>` instead of being skipped as "already consumed".
  localStorage.removeItem(LAST_CONSUMED_AUTH_CODE);
  // Reset in-flight refresh-token redemption state: a redemption that was
  // racing this logout must not resolve into freshly-cleared storage and write
  // back a dead token, and the next sign-in must start from a clean chain.
  resetRefreshState();
  // Also wipe Logto SDK's own localStorage state so the SDK re-evaluates as
  // unauthenticated on next render. Without this, `sdkAuthenticated` stays
  // true after logout and the AppShell gate continues rendering the app.
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('logto:')) localStorage.removeItem(key);
  }
}

export function hasStoredAccessToken(): boolean {
  const tok = localStorage.getItem(QLASH_ACCESS);
  const exp = parseInt(localStorage.getItem(QLASH_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

/**
 * A persisted SESSION exists if we have a still-fresh access token OR a
 * `refresh_token` we can redeem. This is what the auth gate should key off:
 * a returning user (stale access token but valid refresh_token, e.g. after a
 * reboot/refresh >1h since sign-in) is still "logged in" — the access token is
 * silently refreshed rather than bouncing them to Login. Only an explicit
 * logout clears the refresh_token (so `hasStoredSession()` → false → Login).
 */
export function hasStoredSession(): boolean {
  return hasStoredAccessToken() || !!localStorage.getItem(QLASH_REFRESH);
}

/* ------------------------------------------------------------------ */
/* PKCE helpers                                                       */
/* ------------------------------------------------------------------ */

function base64UrlEncode(bytes: Uint8Array): string {
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes).slice(0, length);
}

async function sha256(input: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(buf);
}

/* ------------------------------------------------------------------ */
/* Bridge between Logto SDK and our token storage                      */
/* ------------------------------------------------------------------ */

let accessTokenGetter:
  | ((resource?: string) => Promise<string | undefined>)
  | null = null;

export function setAccessTokenGetter(
  getter: (resource?: string) => Promise<string | undefined>,
): void {
  accessTokenGetter = getter;
}

export async function getLogtoAccessToken(): Promise<string | null> {
  // Prefer manually-stored token (Telegram bridge flow).
  if (hasStoredAccessToken()) {
    return localStorage.getItem(QLASH_ACCESS);
  }
  // Stored token expired — try the refresh_token (also stored in bridge flow).
  // Refreshing with `resource` returns a fresh JWT scoped to the API resource.
  const refreshToken = localStorage.getItem(QLASH_REFRESH);
  if (refreshToken) {
    const refreshed = await refreshLogtoToken();
    if (refreshed) return refreshed;
  }
  // Fall back to Logto SDK (standalone web flow), but NEVER wait forever.
  //
  // The SDK's getAccessToken can hang indefinitely when the SDK itself has no
  // session — which is the normal state in the Telegram bridge flow, where the
  // tokens live under our own keys and the SDK was never signed in. It doesn't
  // reject, it just never settles, so no try/catch can rescue it: the auth gate
  // upstream stayed on "Loading…" forever with nothing on screen.
  // Observed 2026-07-26 from the Telegram app, after a stale access token whose
  // refresh had failed.
  if (!accessTokenGetter) return null;
  try {
    const token = await Promise.race([
      accessTokenGetter(API_RESOURCE),
      new Promise<null>((resolve) => window.setTimeout(() => resolve(null), SDK_TOKEN_TIMEOUT_MS)),
    ]);
    if (token === null) console.warn('[logto] SDK getAccessToken timed out — treating as no token');
    return token ?? null;
  } catch (err) {
    console.warn('[logto] getAccessToken failed:', err);
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Laravel backend token exchange                                     */
/* ------------------------------------------------------------------ */
//
// The Laravel API (api.tournaments.qlash.gg) does NOT accept Logto JWTs
// directly. Calling /tournaments with a raw Logto Bearer token returns a
// stale/cached response (looks like data from an unauthed/legacy code path).
// Mobile-app fixes this by exchanging the Logto token for a Laravel-issued
// token via POST /auth/exchange-token, then using *that* token everywhere.
// We mirror the flow here.

interface ExchangeResponse {
  access_token?: string;
  token?: string;
}

export function getBackendToken(): string | null {
  return localStorage.getItem(BACKEND_TOKEN);
}

export function clearBackendToken(): void {
  localStorage.removeItem(BACKEND_TOKEN);
  armBackendExchange();
}

/**
 * Refreshes the Logto access token using the stored refresh token, with the
 * `resource` parameter so Logto returns a JWT scoped to the API resource
 * (not an opaque token). Use this when the cached access_token may be opaque
 * or expired, before calling the backend exchange.
 */
async function refreshLogtoToken(): Promise<string | null> {
  // Routed through the shared serialized redemption (see redeemRefreshToken)
  // so the main/Laravel refresh can't race qriots/qbot/qsocials and rotate the
  // shared refresh_token out from under them. `redeemRefreshToken` persists the
  // rotated refresh_token centrally; we only own the main access-token cache.
  const { accessToken, expiresIn } = await redeemRefreshToken(env.logtoResource, 'logto');
  if (accessToken) {
    persistTokens({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Shared refresh-token redemption (cross-resource single-flight)      */
/* ------------------------------------------------------------------ */
//
// Logto rotates the refresh_token on EVERY `grant_type=refresh_token`
// redemption (refresh-token rotation is on for this tenant). The Mini App has
// four independent resource getters — main/Laravel (refreshLogtoToken),
// qriots, qbot, qsocials — that each read the SAME `QLASH_REFRESH` and POST it
// to redeem a resource-scoped access token. If any two run concurrently, the
// first redemption rotates R1→R2 and the second's POST of the now-dead R1
// comes back HTTP 400 invalid_grant → that getter returns null → its API slice
// sends no Authorization header → 401 "Failed to load." (with NO server-side
// JWT-verification log, because there's literally no token to verify).
//
// The Friends screen is the heaviest concurrent consumer (5 qsocials queries +
// the presence-heartbeat + profile-sync hooks from AppShell), so qsocials is
// statistically the loser of the race — but qriots/qbot/main are equally
// vulnerable; they just fire far less concurrently today.
//
// Fix: funnel ALL refresh-token redemptions through this one serialized queue.
//
//   * At most ONE refresh_token grant is in flight at any moment, across all
//     resources. A new redemption chains onto the tail of `refreshChain` and
//     only starts once the previous one settles.
//   * Each link re-reads `QLASH_REFRESH` from storage at execution time, so it
//     always redeems the LATEST rotated token (never a stale closure value).
//   * The rotated refresh_token is persisted exactly once, centrally, before
//     the link resolves — so the next link in the chain reads the fresh value.
//   * Per-resource access tokens are preserved: the grant is still made with
//     the caller's `resource`, and the resource-scoped access token is cached
//     by the caller's persist* helper (qriots/qbot/qsocials/main keys stay
//     independent). Only the underlying refresh redemption is serialized.
//   * Same-resource concurrent callers are additionally coalesced onto one
//     in-flight promise (`inFlightByResource`) so we don't redeem N times for
//     N parallel queries hitting the same API — they share one result.

interface RedeemResult {
  /** Resource-scoped access token, or null if the grant failed. */
  accessToken: string | null;
  /** Seconds until the access token expires (for the caller's exp cache). */
  expiresIn: number;
}

// Tail of the serialization chain. Every redemption awaits this before issuing
// its own network request, guaranteeing strictly sequential refresh grants.
let refreshChain: Promise<unknown> = Promise.resolve();

// Per-resource coalescing: concurrent callers for the SAME resource share one
// in-flight promise instead of enqueuing duplicate sequential redemptions.
const inFlightByResource = new Map<string, Promise<RedeemResult>>();

/**
 * Resets all in-flight refresh state. Called on logout so a redemption that
 * was racing the sign-out can't resolve into freshly-cleared storage and
 * resurrect a dead session, and so the next sign-in starts from a clean slate.
 */
export function resetRefreshState(): void {
  refreshChain = Promise.resolve();
  inFlightByResource.clear();
  // «Clean slate» include il contatore dei rifiuti consecutivi: un `1` rimasto da prima
  // del logout renderebbe TERMINALE la prima corsa di rotazione della sessione seguente
  // — cioe' l'utente nuovo pagherebbe il rifiuto del precedente.
  consecutiveInvalidGrants = 0;
  // Anche il rifiuto dello scambio col monolite: e' chiamata al logout, e un rifiuto
  // memorizzato che sopravvivesse al cambio di account seguirebbe l'utente sbagliato.
  armBackendExchange();
}

/* ------------------------------------------------------------------ */
/* Terminal dead-grant handling (one clean re-login, no 401 storm)     */
/* ------------------------------------------------------------------ */
//
// When Logto answers a refresh_token grant with HTTP 400 `invalid_grant`, the
// shared refresh_token (or its grant) is DEAD for EVERY resource — the classic
// account-switch / revoked-grant state. Returning null on each call silently
// 401s every service (and, on dev-bypass services like qgammon, masquerades as
// the shared "Dev" user) while every getter retries → redemption storm. Instead
// we treat such a 400 as terminal: wipe the session and ask the app to drop to a
// clean Login. `deadGrantHandled` makes it fire exactly once per dead session; it
// is re-armed on the next successful interactive login.
//
// NB: a MISSING per-resource grant returns `invalid_target` (one audience), NOT
// `invalid_grant` — that must NOT nuke the whole session.
//
// 🔑 QUESTA RIGA DICE «GRANT», ED E' LA FORMULAZIONE GIUSTA. Per mesi il ramo che tratta
// quel 400 (cerca `invalid_target` piu' sotto in questo stesso file) ne portava un'altra —
// «non esiste su questo tenant» — e le due non si nominavano a vicenda. ⇒ Chi apriva il
// file in un punto capiva una cosa, chi lo apriva nell'altro ne capiva un'altra, e non
// c'era modo di sapere quale fosse aggiornata. ⚠️ E le note sono TRE, non due: c'e' anche
// quella accanto al ramo terminale del redeem («A missing per-resource grant is
// `invalid_target` … so it never triggers the session wipe»), che dice «grant» pure lei.
// Le tre ora si citano: se ne modifichi una, guarda le altre due.
// 🔧 COME LE HO TROVATE, perche' un elenco si esaurisce e un metodo no:
//    git grep -n invalid_target -- src
// I punti che spiegano un codice d'errore condividono per forza il nome del codice che
// spiegano: il grep li mette uno accanto all'altro senza che nessuno debba ricordarsene —
// ed e' cosi' che sono emerse la terza formulazione e l'affermazione di unicita' falsa.
// La sintesi, in una riga: all'`authorize` `invalid_target` parla del TENANT; al
// token/refresh parla della lista di resource di QUEL grant.
//
// 🔴 **DUE invalid_grant CONSECUTIVI, non uno** (2026-09-06, board L-141 + reperti or45).
// Il blocco piu' sotto (redemption) diceva gia' che il ramo terminale e' sicuro «only
// because refresh rotation is DISABLED on the PROD app», e prescriveva letteralmente:
// *«If rotation is ever re-enabled, this branch must first require two consecutive
// invalid_grants — otherwise it becomes a random-logout machine.»*
// ⇒ Sull'app Logto di **STAGING** la rotazione **e' attiva** [M board L-141, 03/09,
// letto dalla console; grado: misurato da altri, non da me]. La superficie di collaudo
// girava quindi esattamente nella condizione dichiarata insicura, e il sintomo
// osservato e' quello predetto: **due sessioni di collaudo morte in ~20-26 minuti, su
// due profili separati, senza sovrapposizione** [M or45, 05/09 22:2x-22:4x].
//
// Perche' un singolo `invalid_grant` NON basta a concludere «grant morto»: `refreshChain`
// serializza le redenzioni **dentro un solo contesto JS**, ma non fra due tab / istanze
// WebView, ne' contro l'SDK Logto che redime fuori dalla catena. Con la rotazione accesa
// ognuna di quelle corse produce un `invalid_grant` **auto-guaribile** — la chiamata
// successiva legge il token gia' ruotato e funziona.
// 🔑 *Il segnale che distingue una CORSA da un GRANT MORTO non e' il primo rifiuto: e'
// il secondo di fila.* Un grant morto fallisce anche al tentativo dopo; una corsa no.
//
// Costo del cambio, detto per intero: una sessione davvero morta impiega una redenzione
// in piu' prima di mostrare il Login (una 400 di round-trip). Il contrario — la sessione
// viva buttata via a caso — costa all'utente il lavoro in corso.
// 📌 Vale anche in PRODUZIONE, dove la rotazione oggi e' spenta: questa riga smette di
// essere una bomba armata dalla configurazione di un altro perimetro, che cambia senza
// preavviso e senza lasciare un segnale in questo repo.

let deadGrantHandled = false;
/**
 * `invalid_grant` consecutivi, azzerati da QUALUNQUE redenzione riuscita. E' il
 * contatore che separa la corsa dal grant morto: la condizione terminale e' `>= 2`.
 */
let consecutiveInvalidGrants = 0;
const DEAD_GRANT_STRIKES = 2;

/* ------------------------------------------------------------------ */
/* Lo scambio Logto→Laravel: un rifiuto non si ritenta a ogni query    */
/* ------------------------------------------------------------------ */
//
// 🔴 Misurato il 2026-09-05 pilotando la TMA con un utente NON collegato al monolite:
// **273 chiamate per un giro di 11 schermate, di cui 124 `exchange-token` tutte 401** —
// 8 tentativi su OGNI route, comprese quelle che di Laravel non hanno bisogno; 14 su
// `/profile`, 18 su `/leaderboards`. Con l'utente collegato: **zero**.
//
// La causa non e' un retry scritto da qualcuno: e' che **il SUCCESSO viene messo in
// cache (`BACKEND_TOKEN`) e il RIFIUTO no.** Ogni consumer che chiede il token trova la
// cache vuota, riprova, e nessuno memorizza il no.
//
// 📌 Un retry senza freno su un errore che NON e' transitorio non e' resilienza: e' un
// amplificatore — e il carico se lo prende il monolite che stiamo cercando di
// alleggerire, nel primo minuto di ogni utente nuovo.
//
// Due freni distinti, perche' curano due cose diverse:
//
//  1. `inFlightBackendExchange` — N chiamanti paralleli condividono UNA promessa. E' lo
//     stesso rimedio gia' adottato sul lato Limulo (`limulo/session.ts`), che era nato
//     serializzato mentre questo non lo e' mai stato.
//  2. `backendRefusedUntil` / `backendRefusalTerminal` — il rifiuto si RICORDA.
//     ⚠️ Non per sempre e non a caso: un `code: 'email_not_verified'` e' **stabile per la
//     sessione** (l'utente non si provisiona da solo mentre guarda una schermata) e vale
//     fino al prossimo login interattivo; qualunque altro fallimento — 503 dell'origine,
//     rete caduta — e' potenzialmente transitorio e vale solo per una **finestra breve**.
//     Confondere i due sarebbe il difetto opposto: un'app che smette di riprovare una
//     cosa che si sarebbe ripresa da sola.
const BACKEND_REFUSAL_WINDOW_MS = 30_000;
let inFlightBackendExchange: Promise<string | null> | null = null;
let backendRefusedUntil = 0;
let backendRefusalTerminal = false;

/** Il rifiuto dello scambio si dimentica: nuovo login, o logout. */
function armBackendExchange(): void {
  inFlightBackendExchange = null;
  backendRefusedUntil = 0;
  backendRefusalTerminal = false;
}
let onSessionExpired: (() => void) | null = null;

/**
 * Register the callback that drops the app to a clean Login when the refresh
 * grant dies. Wired once from useLogtoAuth → dispatch(logout()). Kept as a
 * setter so this framework-agnostic module never imports the React store.
 */
export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

/** Re-arm the terminal-grant guard after a fresh interactive login. */
function armSessionExpiredGuard(): void {
  deadGrantHandled = false;
  consecutiveInvalidGrants = 0;
  // Un login interattivo nuovo puo' aver risolto proprio cio' che lo scambio rifiutava
  // (un'email verificata nel frattempo, un account collegato): il rifiuto memorizzato
  // deve morire qui, o l'utente resterebbe fuori da solo.
  armBackendExchange();
}

/**
 * Terminal `invalid_grant`: the shared refresh_token/grant is dead. Wipe the
 * session (which also empties QLASH_REFRESH, so any queued redemptions bail at
 * the `!refreshToken` guard instead of hammering Logto) and hand off to the
 * registered handler for a clean re-login. Fires once per dead session.
 */
/**
 * `true` quando il grant condiviso e' stato dichiarato MORTO da `handleDeadGrant` —
 * cioe' quando Logto ha risposto `invalid_grant` due volte di fila e la sessione e' gia'
 * stata smontata da qui.
 *
 * 🔑 Esiste perche' `getLogtoAccessToken()` torna `null` **senza dire perche'**: un
 * rifiuto definitivo e una rete che singhiozza escono identici. Chi riceve quel `null`
 * non puo' distinguerli, e per due giorni ha trattato il secondo come il primo —
 * cancellando la sessione (e con essa, via `PER_SERVICE_TOKEN_KEYS`, anche quella Limulo)
 * al primo intoppo di rete.
 * 📌 *Un valore di ritorno che collassa «no» e «non lo so» sposta la decisione su chi ha
 * meno informazioni per prenderla.*
 */
export function isGrantDeclaredDead(): boolean {
  return deadGrantHandled;
}

function handleDeadGrant(label: string): void {
  if (deadGrantHandled) return;
  deadGrantHandled = true;
  console.warn(
    `[${label}] refresh grant is dead (invalid_grant) — clearing session, forcing clean re-login`,
  );
  clearStoredTokens();
  onSessionExpired?.();
}

/**
 * Perform a single refresh_token redemption for `resource`, reading the latest
 * `QLASH_REFRESH` at call time and persisting the rotated refresh_token once.
 * Not exported — always reached through `redeemRefreshToken` so it stays
 * serialized. `label` only affects the warn() namespace for easier debugging.
 */
async function doRedeem(resource: string, label: string): Promise<RedeemResult> {
  const refreshToken = localStorage.getItem(QLASH_REFRESH);
  if (!refreshToken) return { accessToken: null, expiresIn: 0 };

  const tokenUrl = new URL('oidc/token', env.logtoEndpoint).toString();
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: env.logtoAppId,
  });
  // An empty `resource` requests an OP (no-audience) access token — the one the
  // userinfo endpoint accepts to return `identities`. Resource-scoped JWTs don't.
  if (resource) body.set('resource', resource);
  try {
    // ⚠️ 2026-09-06 — SCADENZA COMPLESSIVA. Questa fetch e' l'anello attraverso cui passa
    // ogni chiamata autenticata della app (`services/api.ts:30` la aspetta PRIMA di emettere
    // la richiesta vera): se non settla, la app non mostra un errore — resta ferma, e nessuna
    // schermata puo' dirlo. Alla scadenza `fetchWithTimeout` lancia, e il `catch` qui sotto
    // produce l'esito che gia' esiste per una rete caduta.
    // ⛔ NON e' un `invalid_grant`: `consecutiveInvalidGrants` non si tocca e la sessione
    // resta. Una rete lenta non e' un grant morto.
    const res = await fetchWithTimeout(
      fetch,
      tokenUrl,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      },
      TOKEN_FETCH_TIMEOUT_MS,
    );
    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      // Terminal: a dead/revoked grant or refresh token → force ONE clean
      // re-login instead of null-on-every-call + retry storm. A missing
      // per-resource grant is `invalid_target` (handled as a normal null below),
      // so it never triggers the session wipe.
      // 🔑 Terza delle TRE note di questo file su `invalid_target`, e dice «GRANT» come
      // `:695` — cioe' sta dalla parte giusta. Le altre due: `:695` (la gemella) e il ramo
      // che tratta il 400 piu' sotto (cerca `resourceMemo.markUnavailable`), dove c'e' la
      // spiegazione completa delle DUE cause. Se ne modifichi una, guarda le altre due.
      //
      // The real body, captured from the live incident of 2026-07-16 (a dead
      // grant storming every ~15s across qduels/qbot/qsocials for over an hour):
      //   {"code":"oidc.invalid_grant","message":"Grant request is invalid.",
      //    "error":"invalid_grant","error_description":"grant request is invalid"}
      // Note there is NO `error_detail` — an earlier note in this repo guessed it
      // would say "grant not found". It doesn't. So a dead grant and a rotation
      // race are INDISTINGUISHABLE from the payload.
      //
      // ⚠️ EXTERNAL CONFIG DEPENDENCY — read before touching Logto:
      // per the block comment above, a refresh-token ROTATION RACE also returns
      // 400 invalid_grant, and that case is self-healing (the next call reads the
      // rotated token). Wiping the session there would log users out at random.
      // This is safe TODAY only because refresh rotation is DISABLED on the PROD
      // Logto app (mz8lj85fw0nljzq0sqvay). `refreshChain` serializes redemptions
      // within one JS context, but NOT across two tabs / WebView instances, nor
      // against the Logto SDK redeeming outside the chain (that unification is
      // "Tappa 2", deferred). **If rotation is ever re-enabled, this branch must
      // first require two consecutive invalid_grants (or a distinguishing signal
      // from Logto) — otherwise it becomes a random-logout machine.**
      //
      // ✅ **FATTO il 2026-09-06 (PR #144): la condizione e' due rifiuti consecutivi.**
      // 🔴 **MA il «first» di quella prescrizione e' un ordine FRA RILASCI, non fra
      // commit — e il commento originale non lo diceva.** Su questa piattaforma
      // `main` non e' la produzione (`STATUS.md`: PROD non deployata da giugno), quindi
      // «prima rilassa il ramo» significa **prima che il ramo rilassato sia SERVITO**,
      // non prima che sia mergiato.
      // ⇒ La condizione per accendere `rotateRefreshToken` su un'app Logto e':
      // **il bundle SERVITO da quell'ambiente contiene questa versione del ramo.**
      // Verificabile senza credenziali, cercando nei chunk serviti la stringa
      // `"non cancello la sessione"`.
      // 📌 *Un commento che dice «prima X» non dice mai DOVE. Chi lo legge su `main` lo
      // trova soddisfatto e conclude che si puo' procedere — mentre in produzione gira
      // ancora il ramo vecchio, e accendere li' la rotazione produce esattamente la
      // macchina di logout casuali che la frase voleva impedire.* (rilievo di or45)
      if (res.status === 400 && /"error"\s*:\s*"invalid_grant"/.test(errText)) {
        consecutiveInvalidGrants += 1;
        if (consecutiveInvalidGrants < DEAD_GRANT_STRIKES) {
          // Primo rifiuto: indistinguibile da una corsa di rotazione, e una corsa si
          // guarisce da sola alla chiamata successiva. Non si cancella la sessione qui.
          console.warn(
            `[${label}] invalid_grant ${consecutiveInvalidGrants}/${DEAD_GRANT_STRIKES} — puo' essere una corsa di rotazione, non cancello la sessione`,
          );
          return { accessToken: null, expiresIn: 0 };
        }
        console.warn(
          `[${label}] invalid_grant ${consecutiveInvalidGrants}/${DEAD_GRANT_STRIKES} consecutivi — grant morto`,
          errText.slice(0, 200),
        );
        handleDeadGrant(label);
        return { accessToken: null, expiresIn: 0 };
      }
      // 🔴 `invalid_target` NON e' `invalid_grant`. ⚠️ Questa riga diceva di essere «l'unica
      // riga del file in cui i due 400 si separano»: FALSO, e il controesempio e' `:695`, che
      // fa esattamente lo stesso lavoro («returns `invalid_target` … NOT `invalid_grant`»).
      // 📌 Un'affermazione di UNICITA' in un commento e' la piu' facile da verificare — basta
      // un grep — e la meno verificata, perche' suona come una nota di orientamento e non
      // come un fatto. E fa danno nel verso peggiore: chi la legge SMETTE DI CERCARE.
      // `invalid_grant` parla del GRANT condiviso (sessione morta, o
      // corsa di rotazione); `invalid_target` parla della RESOURCE, e la sessione sta
      // benissimo — ritentare SUBITO dara' sempre lo stesso esito.
      //
      // 🔴 MA LA RESOURCE PUO' MANCARE IN DUE POSTI DIVERSI, e i rimedi sono opposti.
      // Questo commento diceva «non esiste su questo tenant» ed era incompleto nel verso che
      // costa: manda a chiedere all'amministratore di creare una resource **che c'e' gia'**.
      //  1. NON REGISTRATA SUL TENANT      → la crea l'amministratore del tenant.
      //  2. REGISTRATA, ma non nella lista dell'`authorize` di QUESTO grant → RIFARE IL
      //     LOGIN. Nessun amministratore deve toccare niente.
      // [M or49, 2026-09-06 23:19-23:25Z — trasportato, NON verificato da qui]: nel fork
      // oidc-provider di Logto (`resolve_resource.js:28`) il controllo e'
      // `!model.resourceIndicators.has(resource)` e `model` **e' il refresh token** ⇒ QUI,
      // che siamo sul token endpoint, si guarda la lista dell'`authorize` ORIGINALE e non il
      // registro del tenant.
      //
      // ⚠️ [D] E QUESTA E' LA RIGA CHE RICONCILIA IL FILE CON SE STESSO — v. le DUE note
      // gemelle, a `:695` e accanto al ramo terminale del redeem, che dicono entrambe
      // «GRANT», cioe' la spiegazione giusta, e sono rimaste in questo stesso file senza che
      // nessuna delle tre nominasse le altre:
      //   · all'`authorize` il confronto e' col REGISTRO DEL TENANT ⇒ li' `invalid_target`
      //     significa davvero «non registrata» (ed e' cio' che misura la sonda authorize di
      //     `docs/analysis/2026-09-02-tma-stg-pacchetto.md`: quella conclusione REGGE);
      //   · al TOKEN/REFRESH — cioe' qui — il confronto e' con la lista del grant ⇒ valgono
      //     entrambe le cause.
      // 📌 La conoscenza non mancava: era gia' scritta in questo file, in un punto che
      // nessuno collegava a questo. Tre seggi hanno speso ore per riscoprirla.
      //
      // Misurato da or45 il 2026-09-06 su stg-tma: cinque aperture di pannelli della Hub →
      // **sette** POST /oidc/token, di cui **sei** a 400 `invalid_target` per
      // `https://qduels.qlash.gg/api`, che su staging non e' registrata. Il difetto non e' il
      // 400 (e' atteso): e' che la app lo rifa' a ogni montaggio senza ricordarsene.
      // ⚠️ L'ultima frase resta di or45 e non la riscrivo: non l'ho rimisurata. Ma la marco —
      // dal 400 di QUESTA riga «non registrata» non e' piu' decidibile, perche' e' il ramo in
      // cui valgono entrambe le cause. Non citarla come prova che qduels sia assente dal
      // tenant: quella prova, semmai, viene dalla sonda authorize.
      if (res.status === 400 && /"error"\s*:\s*"invalid_target"/.test(errText)) {
        resourceMemo.markUnavailable(resource, 'invalid_target', Date.now());
        console.warn(
          `[${label}] invalid_target su ${resource} — non la richiedo piu' per questa sessione. ` +
            `Due cause possibili: la resource non e' registrata sul tenant (la crea l'amministratore), ` +
            `oppure non era nella lista dell'authorize di QUESTO grant (serve un login nuovo). ` +
            `Dal log non si distinguono: v. il commento qui sopra.`,
        );
        return { accessToken: null, expiresIn: 0 };
      }
      console.warn(`[${label}] refresh failed`, res.status, errText);
      return { accessToken: null, expiresIn: 0 };
    }
    const tokens = (await res.json()) as TokenSet;
    // Una redenzione riuscita e' l'UNICA prova che il grant e' vivo: azzera il contatore,
    // altrimenti due corse lontane fra loro nel tempo si sommerebbero e la seconda
    // ucciderebbe una sessione sana. Il contatore conta rifiuti CONSECUTIVI, non totali.
    consecutiveInvalidGrants = 0;
    // Persist the rotated refresh_token immediately and centrally, so the next
    // link in the chain reads the new value rather than the one we just spent.
    if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
    // Every refresh_token grant re-mints the id_token with fresh claims
    // (regardless of which resource triggered it) — persist it centrally here,
    // the single point through which ALL resource redemptions flow, so the
    // email-verification gate can re-check without a dedicated round trip.
    if (tokens.id_token) localStorage.setItem(QLASH_ID_TOKEN, tokens.id_token);
    if (tokens.access_token) {
      return { accessToken: tokens.access_token, expiresIn: tokens.expires_in };
    }
    return { accessToken: null, expiresIn: 0 };
  } catch (err) {
    console.warn(`[${label}] refresh network error`, err);
    return { accessToken: null, expiresIn: 0 };
  }
}

/**
 * Serialized, cross-resource refresh-token redemption. ALL resource getters go
 * through here. Guarantees a single in-flight refresh_token grant at a time and
 * shares same-resource results. See the block comment above for the rationale.
 */
function redeemRefreshToken(resource: string, label: string): Promise<RedeemResult> {
  // ⚠️ PRIMA di tutto, memoria compresa: questa e' l'unica strada per un token per-servizio,
  // quindi e' l'unico punto in cui basta guardare per fermare la ripetizione.
  //
  // La forma di ritorno e' IDENTICA a quella di un 400 vero (`accessToken: null`): i client
  // per-servizio non distinguono i due casi e non devono — il comportamento visibile non
  // cambia, cambia solo che il server non viene disturbato.
  if (resourceMemo.isUnavailable(resource, Date.now())) {
    return Promise.resolve({ accessToken: null, expiresIn: 0 });
  }
  // Coalesce concurrent callers for the same resource onto one promise.
  const existing = inFlightByResource.get(resource);
  if (existing) return existing;

  // Chain onto the tail so this redemption only fires after any in-flight one
  // settles (and thus after the rotated refresh_token has been persisted).
  const run = refreshChain
    .catch(() => undefined) // a prior link's failure must not break the chain
    .then(() => doRedeem(resource, label));

  // Advance the tail. We deliberately keep the chain alive regardless of this
  // link's outcome (the .catch above guards the next link).
  refreshChain = run;

  inFlightByResource.set(resource, run);
  void run.finally(() => {
    // Only clear if we're still the in-flight entry for this resource (a newer
    // call may have replaced us after we settled).
    if (inFlightByResource.get(resource) === run) inFlightByResource.delete(resource);
  });
  return run;
}

/* ------------------------------------------------------------------ */
/* Qriots-scoped access token                                          */
/* ------------------------------------------------------------------ */
//
// Qriots-scoped access token (audience: https://qriots.qlash.gg/api).
// Cached in localStorage like the main token. Refresh on demand using
// the shared refresh_token; no separate auth flow needed.

function hasFreshQriotsToken(): boolean {
  const tok = localStorage.getItem(QRIOTS_ACCESS);
  const exp = parseInt(localStorage.getItem(QRIOTS_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQriotsToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QRIOTS_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QRIOTS_EXP, String(expAt));
}

/**
 * QBot-scoped access token (audience: https://qbot.qlash.gg).
 * Used by qbotApi.ts. Same caching pattern as the qriots variant.
 */
function hasFreshQbotToken(): boolean {
  const tok = localStorage.getItem(QBOT_ACCESS);
  const exp = parseInt(localStorage.getItem(QBOT_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQbotToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QBOT_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QBOT_EXP, String(expAt));
}

export async function getQbotAccessToken(): Promise<string | null> {
  if (hasFreshQbotToken()) {
    return localStorage.getItem(QBOT_ACCESS);
  }
  // Serialized redemption (shared with main/qriots/qsocials) — see
  // redeemRefreshToken. Central rotation of the refresh_token; local cache of
  // the qbot-scoped access token only.
  const { accessToken, expiresIn } = await redeemRefreshToken(QBOT_RESOURCE, 'logto-qbot');
  if (accessToken) {
    persistQbotToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

/**
 * QSocials-scoped access token (audience: https://qsocials.qlash.gg).
 * Used by socialApi.ts. Same caching pattern as qriots/qbot.
 */
function hasFreshQsocialsToken(): boolean {
  const tok = localStorage.getItem(QSOCIALS_ACCESS);
  const exp = parseInt(localStorage.getItem(QSOCIALS_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQsocialsToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QSOCIALS_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QSOCIALS_EXP, String(expAt));
}

// The Friends screen mounts FIVE qsocials queries at once (friends, requests,
// following, followers, online-feed) plus the AppShell-level presence-heartbeat
// and profile-sync hooks. On a cold cache they all call getQsocialsAccessToken()
// in parallel. A qsocials-only single-flight guard used to dedup *those*, but it
// did NOT coordinate with the main/qriots/qbot getters, which redeem the SAME
// QLASH_REFRESH — so a concurrent main/qriots/qbot redemption could still rotate
// the refresh_token out from under qsocials (400 → null token → 401 "Failed to
// load."). That coordination now lives in redeemRefreshToken(), which serializes
// ALL resources and coalesces same-resource callers. getQsocialsAccessToken()
// just delegates to it.

export async function getQsocialsAccessToken(): Promise<string | null> {
  // 🔴 Il primo cancello, e sta QUI e non nel chiamante: senza, una build con QSocials
  // spento chiederebbe comunque a Logto un token per la resource `qsocials.qlash.gg` —
  // che risponde `invalid_target` quando la resource non e' ottenibile — o perche' il tenant
  // non la conosce, o perche' non era nella lista dell'`authorize` di questo grant (v. il
  // ramo `invalid_target` piu' sopra): in entrambi i casi un errore che somiglia a un
  // difetto di configurazione e non a una scelta.
  // Vedi la nota di `env.featureSocialEnabled`: su staging quella resource non deve
  // essere nemmeno chiesta.
  if (!env.featureSocialEnabled) return null;
  if (hasFreshQsocialsToken()) {
    return localStorage.getItem(QSOCIALS_ACCESS);
  }
  // Serialized + coalesced redemption (shared with main/qriots/qbot) — see
  // redeemRefreshToken. Central rotation of the refresh_token; local cache of
  // the qsocials-scoped access token only.
  const { accessToken, expiresIn } = await redeemRefreshToken(QSOCIALS_RESOURCE, 'logto-qsocials');
  if (accessToken) {
    persistQsocialsToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

/**
 * Returns the user-id string used as the QSocials "userId" (Logto `sub`).
 * QSocials stores friend/follow rows keyed by the JWT sub of the
 * authenticated user. The Mini App needs the same value to know
 * "is this user me?" / build deep links to other users' profiles.
 *
 * Reads from the cached QLASH access token (the one used for the Laravel
 * API). Returns null if no token is present or decoding fails.
 */
export function getMyLogtoSub(): string | null {
  const token = localStorage.getItem(QLASH_ACCESS);
  if (!token) return null;
  const claims = decodeJwtPayload(token);
  const sub = claims?.sub;
  return typeof sub === 'string' ? sub : null;
}

/**
 * Returns an access token whose `aud` claim is `https://qriots.qlash.gg/api`.
 * Used by qriotsApi.ts. Returns null if there's no refresh_token (user
 * not signed in) or if Logto rejects the refresh (resource not authorized).
 */
export async function getQriotsAccessToken(): Promise<string | null> {
  if (hasFreshQriotsToken()) {
    return localStorage.getItem(QRIOTS_ACCESS);
  }
  // Serialized redemption (shared with main/qbot/qsocials) — see
  // redeemRefreshToken. The rotated refresh_token is persisted centrally;
  // here we only cache the qriots-scoped access token + its expiry.
  const { accessToken, expiresIn } = await redeemRefreshToken(QRIOTS_RESOURCE, 'logto-qriots');
  if (accessToken) {
    persistQriotsToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* QDuels-scoped access token                                          */
/* ------------------------------------------------------------------ */
//
// QDuels-scoped access token (audience: https://qduels.qlash.gg/api).
// Used by qduelsApi.ts to send 1v1 duel challenges from the friends list.
// Cached in localStorage like the other resources; minted on demand through
// the shared serialized refresh-token redemption (no separate auth flow).

function hasFreshQduelsToken(): boolean {
  const tok = localStorage.getItem(QDUELS_ACCESS);
  const exp = parseInt(localStorage.getItem(QDUELS_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQduelsToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QDUELS_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QDUELS_EXP, String(expAt));
}

/**
 * Returns an access token whose `aud` claim is `https://qduels.qlash.gg/api`.
 * Used by qduelsApi.ts. Returns null if there's no refresh_token (user not
 * signed in) or if Logto rejects the refresh — most likely because the TMA's
 * Logto app does not yet have the "Qlash API QDuels" API resource granted (in
 * which case the refresh grant returns HTTP 400 and this resolves to null).
 */
export async function getQduelsAccessToken(): Promise<string | null> {
  if (hasFreshQduelsToken()) {
    return localStorage.getItem(QDUELS_ACCESS);
  }
  // Serialized redemption (shared with main/qriots/qbot/qsocials) — see
  // redeemRefreshToken. The rotated refresh_token is persisted centrally;
  // here we only cache the qduels-scoped access token + its expiry.
  const { accessToken, expiresIn } = await redeemRefreshToken(QDUELS_RESOURCE, 'logto-qduels');
  if (accessToken) {
    persistQduelsToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* QChess-scoped access token                                          */
/* ------------------------------------------------------------------ */
//
// QChess-scoped access token (audience: https://qchess.qlash.gg/api).
// Used by qchessApi.ts for chess tournaments/duels/leaderboard. Cached in
// localStorage like the other resources; minted on demand through the shared
// serialized refresh-token redemption (no separate auth flow).

function hasFreshQchessToken(): boolean {
  const tok = localStorage.getItem(QCHESS_ACCESS);
  const exp = parseInt(localStorage.getItem(QCHESS_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQchessToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QCHESS_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QCHESS_EXP, String(expAt));
}

/**
 * Returns an access token whose `aud` claim is `https://qchess.qlash.gg/api`.
 * Used by qchessApi.ts. Returns null if there's no refresh_token (user not
 * signed in) or if Logto rejects the refresh — most likely because the TMA's
 * Logto app does not yet have the "Qlash API QChess" API resource granted (in
 * which case the refresh grant returns HTTP 400 and this resolves to null).
 */
export async function getQchessAccessToken(): Promise<string | null> {
  if (hasFreshQchessToken()) {
    return localStorage.getItem(QCHESS_ACCESS);
  }
  // Serialized redemption (shared with main/qriots/qbot/qsocials/qduels) — see
  // redeemRefreshToken. The rotated refresh_token is persisted centrally;
  // here we only cache the qchess-scoped access token + its expiry.
  const { accessToken, expiresIn } = await redeemRefreshToken(QCHESS_RESOURCE, 'logto-qchess');
  if (accessToken) {
    persistQchessToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* QClans-scoped access token                                          */
/* ------------------------------------------------------------------ */
//
// QClans-scoped access token (audience: https://qclans.qlash.gg/api).
// Used by qclansApi.ts for Clash of Clans stats / tracking / competitions /
// AI coaching. Cached in localStorage like the other resources; minted on
// demand through the shared serialized refresh-token redemption (no separate
// auth flow).

function hasFreshQclansToken(): boolean {
  const tok = localStorage.getItem(QCLANS_ACCESS);
  const exp = parseInt(localStorage.getItem(QCLANS_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQclansToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QCLANS_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QCLANS_EXP, String(expAt));
}

/**
 * Returns an access token whose `aud` claim is `https://qclans.qlash.gg/api`.
 * Used by qclansApi.ts. Returns null if there's no refresh_token (user not
 * signed in) or if Logto rejects the refresh — most likely because the TMA's
 * Logto app does not yet have the "Qlash API QClans" API resource granted (in
 * which case the refresh grant returns HTTP 400 and this resolves to null). In
 * dev that's the expected state until the resource is registered; qclansApi
 * falls back to the X-Dev-Bypass header (env.qclansDevBypass) for local
 * testing against the MacMini.
 */
export async function getQclansAccessToken(): Promise<string | null> {
  if (hasFreshQclansToken()) {
    return localStorage.getItem(QCLANS_ACCESS);
  }
  // Serialized redemption (shared with main/qriots/qbot/qsocials/qduels/qchess)
  // — see redeemRefreshToken. The rotated refresh_token is persisted centrally;
  // here we only cache the qclans-scoped access token + its expiry.
  const { accessToken, expiresIn } = await redeemRefreshToken(QCLANS_RESOURCE, 'logto-qclans');
  if (accessToken) {
    persistQclansToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

function hasFreshQbankToken(): boolean {
  const tok = localStorage.getItem(QBANK_ACCESS);
  const exp = parseInt(localStorage.getItem(QBANK_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

/**
 * Returns a Bearer token QBank will accept.
 *
 * SPIKE (dev only): mints a token from QBank's INTERNAL test OIDC
 * (`POST /oidc/test-token`, HS256) for the configured dev sub, with the
 * `qbank:wallet:read` role. This deliberately bypasses Logto — it works only
 * while QBank runs locally with ENABLE_OIDC=true. Mirrors the qclans dev-bypass
 * idea (env-gated, dev only).
 *
 * PROD: mint a real Logto token scoped to the "Qlash API QBank" resource
 * (aud https://qbank.qlash.gg/api) via the shared serialized redeemRefreshToken —
 * same path as qriots/qbot/qclans. Our prod QBank fork validates that audience;
 * the resource scopes are named qbank:* (== the roles), so no bridge is needed.
 * Returns null (card → "—") until the user's Logto token is granted the
 * qbank:wallet:read scope (the "qbank:wallet:read" role assigned in Logto).
 */
export async function getQbankAccessToken(): Promise<string | null> {
  if (hasFreshQbankToken()) {
    return localStorage.getItem(QBANK_ACCESS);
  }
  if (env.qbankDevTestOidc && env.qbankDevSub) {
    try {
      const res = await fetch(`${env.qbankApiUrl}/oidc/test-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sub: env.qbankDevSub, roles: ['qbank:wallet:read'] }),
      });
      if (!res.ok) {
        console.warn('[qbank-dev] test-token failed', res.status);
        return null;
      }
      const data = (await res.json()) as { access_token: string; expires_in: number };
      localStorage.setItem(QBANK_ACCESS, data.access_token);
      localStorage.setItem(QBANK_EXP, String(Math.floor(Date.now() / 1000) + data.expires_in));
      return data.access_token;
    } catch (err) {
      console.warn('[qbank-dev] test-token error', err);
      return null;
    }
  }
  // PROD: real Logto token for the wallet resource via the shared serializer.
  const { accessToken, expiresIn } = await redeemRefreshToken(QBANK_RESOURCE, 'logto-qbank');
  if (accessToken) {
    localStorage.setItem(QBANK_ACCESS, accessToken);
    localStorage.setItem(QBANK_EXP, String(Math.floor(Date.now() / 1000) + expiresIn));
    return accessToken;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* QGammon-scoped access token                                         */
/* ------------------------------------------------------------------ */
//
// QGammon-scoped access token (audience: https://qgammon.qlash.gg/api).
// Used by the `/backgammon` surface — the qgammon-gameserver (Socket.IO :4001)
// and qgammon-api (REST :4000) both accept it as `Authorization: Bearer`
// (REST) / `auth.accessToken` (handshake), validate the audience, and PROVISION
// a per-user row keyed by the JWT `sub` (logtoSub). This per-user identity is
// the FIX for the shared dev-bypass "Dev" user bug (two humans → same userId →
// 409 already_queued, can never pair). Cached in localStorage like the other
// resources; minted on demand through the shared serialized refresh-token
// redemption (no separate auth flow).

function hasFreshQgammonToken(): boolean {
  const tok = localStorage.getItem(QGAMMON_ACCESS);
  const exp = parseInt(localStorage.getItem(QGAMMON_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQgammonToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QGAMMON_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QGAMMON_EXP, String(expAt));
}

/**
 * Returns an access token whose `aud` claim is `https://qgammon.qlash.gg/api`,
 * or null when no session exists or Logto rejects the refresh.
 *
 * The "Qlash API QGammon" resource exists in PROD Logto since 2026-06-11 (the
 * QGammon backoffice SPA mints tokens for it), so the on-demand refresh grant
 * succeeds for any signed-in user; scopes the user lacks are silently dropped.
 * Callers fall back to the dev-bypass env when this resolves to null (dev), and
 * surface an error only when neither is available. Same path as
 * qriots/qbot/qsocials/qduels/qchess/qclans.
 */
export async function getQgammonToken(): Promise<string | null> {
  if (hasFreshQgammonToken()) {
    return localStorage.getItem(QGAMMON_ACCESS);
  }
  // Serialized redemption (shared with every other resource) — see
  // redeemRefreshToken. The rotated refresh_token is persisted centrally;
  // here we only cache the qgammon-scoped access token + its expiry.
  const { accessToken, expiresIn } = await redeemRefreshToken(QGAMMON_RESOURCE, 'logto-qgammon');
  if (accessToken) {
    persistQgammonToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* QChinesePoker-scoped access token                                   */
/* ------------------------------------------------------------------ */
//
// QChinesePoker-scoped access token (audience: https://qchinesepoker.qlash.gg/api).
// Used by the `/chinesepoker` surface — the qcp-gameserver (Socket.IO :4101,
// handshake `auth.token`) and qcp-api (REST :4100, `Authorization: Bearer`)
// both accept it, validate the audience, and resolve the caller by JWT `sub`
// (per-user identity — what makes ranked-queue pairing + friend invites work
// with distinct subs). Cached in localStorage like the other resources; minted
// on demand through the shared serialized refresh-token redemption.

function hasFreshQcpToken(): boolean {
  const tok = localStorage.getItem(QCP_ACCESS);
  const exp = parseInt(localStorage.getItem(QCP_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQcpToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QCP_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QCP_EXP, String(expAt));
}

/**
 * Returns an access token whose `aud` claim is `https://qchinesepoker.qlash.gg/api`,
 * or null when no session exists or Logto rejects the refresh. Same on-demand
 * path as qriots/qbot/qsocials/qduels/qchess/qclans/qgammon.
 */
export async function getQcpToken(): Promise<string | null> {
  if (hasFreshQcpToken()) {
    return localStorage.getItem(QCP_ACCESS);
  }
  const { accessToken, expiresIn } = await redeemRefreshToken(QCP_RESOURCE, 'logto-qcp');
  if (accessToken) {
    persistQcpToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

function decodeJwtPart(part: string): Record<string, unknown> | null {
  try {
    const norm = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = norm + '='.repeat((4 - (norm.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  return decodeJwtPart(parts[1]);
}

/**
 * Pull the `roles` claim from the currently-stored Logto access token.
 *
 * Logto's PROD tenant has a Custom JWT script that injects user roles into
 * every access token (see `admin-account.qlash.gg → JWT personalizzato →
 * Token di accesso utente`). For Luca that means the JWT carries something
 * like:
 *   { roles: ["user", "administrator", "backoffice", "beta", ...] }
 *
 * We consume those roles client-side because Laravel's `/users/me` does NOT
 * expose `is_admin` to mini-app callers (it gates the field behind a
 * `X-ADMIN` header used by the backoffice) and has no `beta_tester` column
 * at all — see `qlashmaker/app/Http/Resources/User/UserResource.php`.
 * Reading the token claim is the only available path until the upcoming
 * fitp-api replaces the monolith.
 *
 * Returns `[]` on decode failure or when no token is stored — callers
 * should treat absence as no privilege.
 */
export function getLogtoRolesFromAccessToken(): string[] {
  const token = localStorage.getItem(QLASH_ACCESS);
  if (!token) return [];
  const claims = decodeJwtPayload(token);
  const roles = claims?.roles;
  if (!Array.isArray(roles)) return [];
  return roles.filter((r): r is string => typeof r === 'string');
}

function decodeJwtHeader(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  return decodeJwtPart(parts[0]);
}

/** L'esito dello scambio, col motivo: `null` non basta a decidere se ritentare. */
interface ExchangeOutcome {
  readonly token: string | null;
  /** 0 = la richiesta non e' mai arrivata (rete). */
  readonly status: number;
  readonly code: string | null;
}

/**
 * I rifiuti STABILI per la sessione: ritentarli non puo' cambiarne l'esito, perche' cio'
 * che manca non si risolve da solo mentre l'utente guarda una schermata.
 *
 * ⚠️ Elenco chiuso e nominato di proposito. La regola opposta — «tutti i 4xx sono
 * stabili» — sarebbe piu' comoda e sbagliata: un 401 da token scaduto si cura da se' al
 * refresh successivo, e trattarlo come terminale lascerebbe fuori un utente legittimo.
 */
const TERMINAL_EXCHANGE_CODES = new Set(['email_not_verified']);

async function postExchange(token: string): Promise<ExchangeOutcome> {
  const exchangeUrl = `${env.apiUrl}/auth/exchange-token`;
  const tokenKind = token.startsWith('eyJ') ? 'JWT' : 'opaque';
  if (tokenKind === 'JWT') {
    const header = decodeJwtHeader(token);
    const claims = decodeJwtPayload(token);
    if (header) dbg(`JWT header: ${JSON.stringify(header)}`);
    if (claims) {
      dbg(`JWT payload: ${JSON.stringify(claims)}`);
    } else {
      dbg('JWT decode failed');
    }
  }
  dbg(`exchange-token POST kind=${tokenKind} len=${token.length}`);
  try {
    // ⚠️ Stessa scadenza dell'altro anello (`doRedeem`), stessa ragione: il `catch` qui sotto
    // ritorna `status: 0`, che non e' fra i `TERMINAL_EXCHANGE_CODES` ⇒ arma
    // `backendRefusedUntil` per la finestra breve, come farebbe un 503. La scadenza non
    // introduce nessun ramo nuovo: riusa quello gia' pensato per l'origine irraggiungibile.
    const res = await fetchWithTimeout(
      fetch,
      exchangeUrl,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          platform: 'telegram',
        },
        body: JSON.stringify({ token }),
      },
      TOKEN_FETCH_TIMEOUT_MS,
    );
    if (!res.ok) {
      const errBody = await res.text().catch(() => '');
      dbg(`exchange-token FAIL ${res.status} ${errBody.slice(0, 150)}`);
      // ⚠️ Il corpo lo leggevamo gia' e lo buttavamo: `null` per un 401 «utente non
      // collegato», per un 401 «token scaduto», per un 503 e per una caduta di rete.
      // Non distinguevamo perche' SCARTAVAMO, non perche' l'informazione mancasse — e
      // senza quella distinzione non si puo' decidere cosa vale la pena ritentare.
      let code: string | null = null;
      try {
        code = (JSON.parse(errBody) as { code?: string }).code ?? null;
      } catch {
        code = null;
      }
      return { token: null, status: res.status, code };
    }
    const data = (await res.json()) as ExchangeResponse;
    const backendToken = data.access_token ?? data.token ?? null;
    dbg(`exchange-token OK got_token=${!!backendToken}`);
    return { token: backendToken, status: res.status, code: null };
  } catch (err) {
    dbg(`exchange-token NETWORK_ERR ${err instanceof Error ? err.message : String(err)}`);
    return { token: null, status: 0, code: null };
  }
}

export async function exchangeLogtoForBackend(): Promise<string | null> {
  // I due freni, PRIMA di qualunque rete. L'ordine conta: il rifiuto terminale non
  // scade, la finestra si'.
  if (backendRefusalTerminal) return null;
  if (Date.now() < backendRefusedUntil) return null;

  // Un solo scambio alla volta: l'apertura di una schermata fa partire N query insieme,
  // e senza questo ognuna ne farebbe uno suo. E' la stessa serializzazione che il lato
  // Limulo ha da sempre e questo non ha mai avuto.
  inFlightBackendExchange ??= runBackendExchange().finally(() => {
    inFlightBackendExchange = null;
  });
  return inFlightBackendExchange;
}

async function runBackendExchange(): Promise<string | null> {
  const initial = await getLogtoAccessToken();
  if (!initial) return null;

  // First try with the cached Logto access token.
  let outcome = await postExchange(initial);

  // If the cached token is opaque/stale, refresh it (with `resource`) to get
  // a fresh JWT, then retry the exchange.
  //
  // ⚠️ SOLO su 401. Il refresh cura una causa sola — il token che abbiamo in mano — e
  // ritentarlo su un 503 e' lavoro che l'utente paga in attesa (misurato 05/09: ~19 s e un
  // grant Logto per schermata, con il monolite di staging spento) e che, con la rotazione dei
  // refresh token accesa, brucerebbe un refresh token per niente. Il perche' completo e la
  // regola stanno in `lib/exchangeRetry.ts`, dove sono esercitabili sotto Node.
  if (!outcome.token && shouldRefreshAndRetryExchange(outcome.status)) {
    const refreshed = await refreshLogtoToken();
    if (refreshed) outcome = await postExchange(refreshed);
  }

  if (outcome.token) {
    localStorage.setItem(BACKEND_TOKEN, outcome.token);
    return outcome.token;
  }

  // Il no si ricorda. Terminale se il motivo dice che ritentare non serve; altrimenti
  // per una finestra breve, cosi' un 503 o una rete caduta si riprendono da soli.
  if (outcome.code !== null && TERMINAL_EXCHANGE_CODES.has(outcome.code)) {
    backendRefusalTerminal = true;
    dbg(`exchange-token rifiuto TERMINALE (${outcome.code}): non si ritenta fino al prossimo login`);
  } else {
    backendRefusedUntil = Date.now() + BACKEND_REFUSAL_WINDOW_MS;
    dbg(`exchange-token rifiuto TEMPORANEO (status=${outcome.status}): fermo ${BACKEND_REFUSAL_WINDOW_MS}ms`);
  }
  return null;
}

/**
 * Returns the cached Laravel backend token if present, otherwise performs the
 * Logto→backend exchange and caches the result.
 */
export async function getApiAccessToken(): Promise<string | null> {
  const cached = getBackendToken();
  if (cached) return cached;
  return exchangeLogtoForBackend();
}

/* ------------------------------------------------------------------ */
/* Telegram-aware sign-in                                             */
/* ------------------------------------------------------------------ */

export function getRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

/**
 * True when running inside a Telegram WebView (mobile/desktop client) — i.e.
 * `Telegram.WebApp.initData` is populated. Returns false in plain browser
 * (where the standard Logto redirect flow works).
 */
/**
 * localStorage marker set when sign-in runs INSIDE the mini app's own webview
 * (desktop path) rather than through the browser→deep-link bridge. The callback
 * reads it to know which of the two it must complete.
 */
export const AUTH_INAPP = 'qlash.auth_inapp';

/**
 * True only on the mobile Telegram clients, where the browser→`t.me` deep-link
 * bridge actually works.
 *
 * The bridge relies on the OS handing the deep link back to the Telegram app,
 * which relaunches the mini app carrying `start_param`. On DESKTOP that chain is
 * broken by design: `retrieveLaunchParams()` reads the parameters fixed when the
 * mini app was launched, and a deep link fired while it is already open only
 * focuses the existing window — it never re-launches it, so `start_param` never
 * changes and `consumeAuthStartParam` keeps reporting "no tgWebAppStartParam
 * present" forever. Observed on Telegram macOS, 2026-07-26.
 */
export function isTelegramMobileClient(): boolean {
  try {
    const p = retrieveLaunchParams().tgWebAppPlatform;
    return p === 'android' || p === 'android_x' || p === 'ios';
  } catch {
    return false;
  }
}

export function isTelegramWebView(): boolean {
  try {
    const lp = retrieveLaunchParams();
    return !!lp.tgWebAppData?.user;
  } catch {
    return false;
  }
}

/**
 * Builds the Logto OIDC authorize URL with PKCE state stored locally for the
 * subsequent token exchange.
 */
async function buildAuthUrl(): Promise<string> {
  const verifier = randomString(64);
  const state = randomString(32);
  const challenge = base64UrlEncode(await sha256(verifier));

  localStorage.setItem(PKCE_VERIFIER, verifier);
  localStorage.setItem(PKCE_STATE, state);

  const url = new URL('oidc/auth', env.logtoEndpoint);
  url.searchParams.set('client_id', env.logtoAppId);
  url.searchParams.set('redirect_uri', getRedirectUri());
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', SCOPE_STRING);
  // Request every resource at sign-in so the issued refresh_token is
  // authorised for all audiences. Logto allows multiple `resource` params
  // (RFC 8707); empirically verified 2026-05-22 that this does not affect
  // redirect_uri validation. Without an audience here, the on-demand refresh
  // grant for that resource (e.g. getQriotsAccessToken) returns 400.
  // NOTE (qduels): listing QDUELS_RESOURCE here only asks Logto to scope the
  // refresh_token to it — Logto still requires the "Qlash API QDuels" API
  // resource to be GRANTED to this app server-side. If that grant is missing,
  // the qduels audience is silently dropped from the token and the on-demand
  // refresh in getQduelsAccessToken() comes back 400 → null. See qduelsApi.ts.
  // Keep the authorize request and Logto SDK config on the same source of
  // truth. Diverging lists break only one platform at a time and are difficult
  // to reproduce.
  for (const resource of AUTHORIZED_RESOURCES) {
    url.searchParams.append('resource', resource);
  }
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', state);
  // `login consent` — two OIDC prompts, both required:
  //   • `login`   forces re-authentication (ask for credentials) every authorize
  //     instead of silently reusing an existing IdP session. REQUIRED in the
  //     Telegram bridge: sign-out runs inside the WebView and clears our local
  //     tokens, but the Logto session cookie lives in the *system browser* (a
  //     cookie jar we can't reach from the WebView). Without it, the post-logout
  //     sign-in lands on a still-alive session and skips the credential prompt.
  //   • `consent` re-generates Logto's stored consent grant (the set of
  //     authorized API resources) to include ALL currently-requested resources.
  //     Logto persists the grant per user+app and reuses it across logins, so
  //     `login` alone keeps a STALE grant — any resource added after the user's
  //     last consent (here: qsocials, qduels) is missing from it, and the
  //     on-demand refresh grant for that resource returns 400 → null token →
  //     401 "Missing Authorization header" on the qsocials/qduels calls.
  //     (First-party apps auto-consent: no extra screen is shown.)
  url.searchParams.set('prompt', 'login consent');
  return url.toString();
}

/**
 * Telegram-side sign-in: opens Logto in the user's system browser, where
 * Google/Apple OAuth aren't blocked. The bridge in <AuthCallback> then
 * deep-links back to the bot with the auth code in `start_param`.
 */
export async function startTelegramSignIn(): Promise<void> {
  const url = await buildAuthUrl();

  // DESKTOP / Telegram Web: navigate this very webview to Logto and come back
  // to our own /auth/callback. The redirect_uri is identical to the bridge's
  // (`getRedirectUri()`), so nothing new has to be whitelisted in Logto — only
  // who completes the exchange changes, and the callback learns that from the
  // marker below. We do NOT open the system browser here: on desktop nothing
  // could bring the code back into an already-running mini app.
  if (!isTelegramMobileClient()) {
    localStorage.setItem(AUTH_INAPP, '1');
    dbg(`signIn: in-app redirect (desktop) → ${url.slice(0, 80)}...`);
    window.location.href = url;
    return;
  }

  // MOBILE: the browser→deep-link bridge, unchanged. Google refuses OAuth in
  // mobile embedded webviews (`disallowed_useragent`), so the system browser is
  // the only way, and the OS can hand the deep link back to the Telegram app.
  localStorage.removeItem(AUTH_INAPP);
  dbg(`signIn: openLink → ${url.slice(0, 80)}...`);
  const result = openLink.ifAvailable(url);
  dbg(`signIn: openLink.ifAvailable returned ${JSON.stringify(result)}`);
}

/**
 * Exchanges an authorization code received via `start_param` (after the
 * external-browser → t.me deep-link bridge) for tokens, and stores them.
 */
export async function exchangeAuthCode(code: string): Promise<boolean> {
  const verifier = localStorage.getItem(PKCE_VERIFIER);
  if (!verifier) {
    dbg('exchange FAIL: PKCE verifier missing');
    return false;
  }

  const tokenUrl = new URL('oidc/token', env.logtoEndpoint).toString();
  const redirectUri = getRedirectUri();
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    code_verifier: verifier,
    redirect_uri: redirectUri,
    client_id: env.logtoAppId,
    // RFC 8707 resource indicator — needed for Logto to issue a JWT scoped to
    // the API resource. Without it, access_token comes back opaque and our
    // Laravel /auth/exchange-token rejects it ("Token verification failed").
    // The qriots-scoped token is NOT requested here by design; it is minted
    // on-demand via getQriotsAccessToken() using the same refresh_token.
    resource: env.logtoResource,
  });

  dbg(`exchange POST ${tokenUrl} code=${code.slice(0, 10)}.. ver_len=${verifier.length} redir=${redirectUri}`);

  let res: Response;
  try {
    res = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
  } catch (err) {
    dbg(`exchange FAIL: network/CORS ${err instanceof Error ? err.message : String(err)}`);
    return false;
  }

  if (!res.ok) {
    const errBody = await res.text().catch(() => '<unreadable>');
    dbg(`exchange FAIL: HTTP ${res.status} ${errBody.slice(0, 200)}`);
    return false;
  }

  const tokens = (await res.json()) as TokenSet;
  // Un login nuovo puo' essere su un tenant diverso (o sullo stesso, dopo che qualcuno ci ha
  // registrato la resource che mancava): quello che sapevamo delle resource assenti non vale
  // piu'. Si dimentica QUI, sullo scambio del codice, e non dentro `persistTokens` — che
  // gira anche a ogni refresh, e la' azzerare la memoria a ogni giro la renderebbe inutile.
  resourceMemo.clear();
  if (!tokens.access_token) {
    dbg('exchange FAIL: no access_token in response');
    return false;
  }
  dbg(`exchange OK: token_len=${tokens.access_token.length} expires_in=${tokens.expires_in}`);

  // We've issued an `openid offline_access` request but Logto returns the
  // *opaque* access token only when no `resource` is specified. Since we passed
  // `resource`, this token is the JWT scoped to the API resource — exactly
  // what RTK Query needs.
  persistTokens(tokens);
  if (tokens.id_token) localStorage.setItem(QLASH_ID_TOKEN, tokens.id_token);
  // Fresh interactive login: drop any per-service token caches from a previous
  // account so the new user can't inherit them (e.g. acting as the old user on
  // qgammon until they expire), and re-arm the terminal dead-grant guard so a
  // future dead grant can force a clean re-login again.
  clearPerServiceTokenCaches();
  armSessionExpiredGuard();

  localStorage.removeItem(PKCE_VERIFIER);
  localStorage.removeItem(PKCE_STATE);
  return true;
}

/**
 * Reads the `auth_<code>` start_param when the mini-app is reopened via the
 * t.me deep-link bridge, exchanges the code for tokens, and returns true on
 * success. Returns null if no auth start_param is present.
 */
export async function consumeAuthStartParam(): Promise<boolean | null> {
  let startParam: string | undefined;
  try {
    startParam = retrieveLaunchParams().tgWebAppStartParam;
  } catch (err) {
    dbg(`consume FAIL: retrieveLaunchParams threw ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
  if (!startParam) {
    dbg('consume: no tgWebAppStartParam present');
    return null;
  }
  if (!startParam.startsWith('auth_')) {
    dbg(`consume: startParam is not auth_*: ${startParam.slice(0, 30)}`);
    return null;
  }
  const code = startParam.slice('auth_'.length);
  return await exchangeAuthCode(code);
}

/* ------------------------------------------------------------------ */
/* Email verification (onboarding gate)                                */
/* ------------------------------------------------------------------ */

/**
 * `email_verified` claim from the stored Logto id_token (bridge flow persists
 * it at code exchange + refresh). `null` = unknown (no id_token stored — e.g.
 * sessions created before this feature, or standalone SDK flow) — callers
 * MUST treat unknown as "don't block".
 */
export function getStoredEmailVerified(): boolean | null {
  const raw = localStorage.getItem(QLASH_ID_TOKEN);
  if (!raw) return null;
  try {
    const payload = JSON.parse(
      atob(raw.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as { email_verified?: boolean };
    return typeof payload.email_verified === 'boolean' ? payload.email_verified : null;
  } catch {
    return null;
  }
}

/**
 * Raw ID token del bridge Telegram, da inoltrare soltanto a verificatori server-side.
 * Il client non ne convalida firma, issuer o audience e non deve usarlo come prova:
 * il BFF Limulo esegue tutte queste verifiche prima di leggere i claim d'identita'.
 */
export function getStoredIdentityToken(): string | null {
  const raw = localStorage.getItem(QLASH_ID_TOKEN)?.trim();
  return raw || null;
}

/**
 * `email` claim from the stored Logto id_token — the address the email gate
 * asks the user to confirm. Null when unknown (no id_token / no claim).
 */
export function getStoredEmailClaim(): string | null {
  const raw = localStorage.getItem(QLASH_ID_TOKEN);
  if (!raw) return null;
  try {
    const payload = JSON.parse(
      atob(raw.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as { email?: string };
    return typeof payload.email === 'string' && payload.email ? payload.email : null;
  } catch {
    return null;
  }
}

/**
 * Fresh re-check after the user says "I've verified": refresh the token pair
 * (Logto re-issues the id_token with current claims), then fall back to the
 * OIDC userinfo endpoint. Best-effort on both legs; `null` = unknown.
 */
export async function recheckEmailVerified(): Promise<boolean | null> {
  const refreshed = await refreshLogtoToken();
  if (refreshed) {
    const fromToken = getStoredEmailVerified();
    if (fromToken !== null) return fromToken;
  }
  try {
    const token = await getLogtoAccessToken();
    if (!token) return null;
    const res = await fetch(new URL('oidc/me', env.logtoEndpoint).toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { email_verified?: boolean };
    return typeof data.email_verified === 'boolean' ? data.email_verified : null;
  } catch {
    return null;
  }
}

export interface LinkedSocialIdentity {
  /** Connector target, e.g. "google" | "apple" | "discord" | "twitch". */
  target: string;
  /** Best-effort display name/username/handle from the connector, if present. */
  username?: string;
  /** Associated email from the connector, if present (used for google/apple). */
  email?: string;
}

function pickStr(v: unknown): string | undefined {
  return typeof v === 'string' && v ? v : undefined;
}

/**
 * A no-resource (OP) access token minted from the shared refresh_token, for the
 * userinfo endpoint — resource-scoped JWTs don't return `identities`. Goes
 * through the same serialized `redeemRefreshToken` chain (rotation is disabled
 * on the PROD Logto app, so the extra grant is safe). Cached in memory for its
 * lifetime; cleared on logout/account-switch. Best-effort — `null` on failure.
 */
async function getOpAccessToken(): Promise<string | null> {
  const now = Math.floor(Date.now() / 1000);
  if (opTokenCache && opTokenCache.exp - 60 > now) return opTokenCache.token;
  const { accessToken, expiresIn } = await redeemRefreshToken('', 'logto-userinfo');
  if (accessToken) {
    opTokenCache = { token: accessToken, exp: now + expiresIn };
    return accessToken;
  }
  return null;
}

/**
 * Read the user's linked social identities from the Logto userinfo endpoint
 * (the app requests `UserScope.Identities`). Returns a map keyed by connector
 * target (`google`, `apple`, `discord`, `twitch`, …). Best-effort — `{}` on any
 * failure. Note: reflects Logto-managed identities only; Telegram is NOT a Logto
 * connector (read it from the Mini App `initData` via `useTelegram().user`).
 */
export async function getLinkedSocialIdentities(): Promise<
  Record<string, LinkedSocialIdentity>
> {
  try {
    // Prefer the OP (no-resource) token — userinfo only returns `identities` for
    // it, not for a resource-scoped JWT. Fall back to the resource token.
    const token = (await getOpAccessToken()) ?? (await getLogtoAccessToken());
    if (!token) return {};
    const res = await fetch(new URL('oidc/me', env.logtoEndpoint).toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return {};
    const data = (await res.json()) as {
      identities?: Record<string, { details?: Record<string, unknown> }>;
    };
    if (env.isDev) {
      // Debug (visible in eruda on dev-tma): does userinfo return `identities`?
      // If keys omit "identities", the resource-scoped token isn't returning
      // them and we need a non-resource token for userinfo.
      console.log(
        '[linked-identities] userinfo keys:',
        Object.keys(data),
        'identities:',
        data.identities ?? null,
      );
    }
    const out: Record<string, LinkedSocialIdentity> = {};
    for (const [target, id] of Object.entries(data.identities ?? {})) {
      const d = (id?.details ?? {}) as Record<string, unknown>;
      const raw = (d.rawData ?? {}) as Record<string, unknown>;
      out[target] = {
        target,
        username: pickStr(d.name) ?? pickStr(raw.username) ?? pickStr(raw.login),
        email: pickStr(d.email) ?? pickStr(raw.email),
      };
    }
    return out;
  } catch {
    return {};
  }
}

/* ------------------------------------------------------------------ */
/* Quest-scoped access token                                           */
/* ------------------------------------------------------------------ */
//
// Token con `aud` = https://quest.qlash.gg/api, per la sezione Missioni.
// Stesso percorso on-demand di qriots/qbot/qsocials/qduels/qchess/qclans/
// qgammon/qcp: riscatto serializzato del refresh_token, cache in localStorage.
//
// ⚠️ DUE COSE CHE QUEST FA E QUASI NESSUN ALTRO SERVIZIO FA.
//
// 1. Quest fa rispettare gli SCOPE sul token utente, non solo l'audience —
//    e' il terzo dopo QBank e QGammon (`quest-api/src/auth/authMiddleware.ts:31`
//    @ b24b03d, confronto letterale sull'array). E Logto mette gli scope di una
//    resource nel token SOLO SE SONO RICHIESTI nella authorize call: la
//    concessione RBAC da sola non basta, e gli scope non richiesti escono con il
//    claim `scope` VUOTO. ⇒ `quest:missions:read` (dal 29/8) e `quest:missions:claim`
//    (dal 30/8, col bottone di riscossione) stanno in `logtoConfig.scopes` E in
//    `SCOPE_STRING` — nomi PIENI, col prefisso di servizio: un token con
//    `missions:read` passa la firma e prende 403. Che le due liste restino
//    specchio e' asserito da `tools/verify-missions.ts`, non affidato alla memoria.
//    Sintomo se si sbaglia: 403 con `{"missing":[...]}`, che dice quale manca.
//
//    🔴 E il riscatto qui sotto NON puo' rimediare a uno scope mancante: `doRedeem`
//    non manda alcun parametro `scope`, e non servirebbe — l'insieme degli scope e'
//    fissato dalla authorize call. Un refresh token emesso PRIMA che una riga qui
//    sopra esistesse non l'acquista mai: serve un nuovo accesso interattivo.
//
// 2. `X-Dev-Bypass` NON e' una via d'uscita in produzione: l'authMiddleware lo
//    accetta solo con `NODE_ENV === 'development'` e in prod vale `production`
//    (verificato in vivo: 401 anche col bypass). Percio' qui non esiste alcun
//    ramo di bypass — non e' una dimenticanza, e' che non ne esiste uno che
//    funzioni, e scriverlo darebbe una falsa sicurezza.

function hasFreshQuestToken(): boolean {
  const tok = localStorage.getItem(QUEST_ACCESS);
  const exp = parseInt(localStorage.getItem(QUEST_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQuestToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QUEST_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QUEST_EXP, String(expAt));
}

/**
 * Token con audience `https://quest.qlash.gg/api`, oppure null se non c'e'
 * sessione o se Logto rifiuta il riscatto.
 *
 * Finche' la resource non e' CONCESSA alle app TMA, Logto la ignora in silenzio
 * e il riscatto torna 400 -> null: la sezione mostra "non disponibile" invece di
 * dati, che e' il degrado voluto. E' anche la ragione per cui la concessione
 * mancante e' recuperabile mentre una resource inesistente non lo sarebbe.
 */
export async function getQuestAccessToken(): Promise<string | null> {
  if (hasFreshQuestToken()) {
    return localStorage.getItem(QUEST_ACCESS);
  }
  const { accessToken, expiresIn } = await redeemRefreshToken(QUEST_RESOURCE, 'logto-quest');
  if (accessToken) {
    persistQuestToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}
